import { useState } from 'react';

export default function ContactForm() {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', content: '' });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('loading');

    const form = e.currentTarget;
    const formPayload = new FormData(form);
    const honeypot = formPayload.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setStatus('success');
      setMessage('感谢您的留言，我们会尽快回复！');
      setFormData({ name: '', email: '', subject: '', content: '' });
      return;
    }

    const contentValue = (formPayload.get('content') as string) || '';
    if (contentValue.length > 500) {
      setStatus('error');
      setMessage('留言内容不能超过500个字符');
      return;
    }

    try {
      const res = await fetch('https://readdy.ai/api/form/d9tvued3pcjqs2fcsjmg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          name: (formPayload.get('name') as string) || '',
          email: (formPayload.get('email') as string) || '',
          subject: (formPayload.get('subject') as string) || '',
          content: contentValue,
        }).toString(),
      });

      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (res.ok && parsed?.code === 'OK') {
        setStatus('success');
        setMessage('感谢您的留言，我们会尽快回复！');
        setFormData({ name: '', email: '', subject: '', content: '' });
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || '提交失败，请稍后再试';
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setStatus('error');
          setMessage('提交失败，请稍后再试');
        } else {
          setStatus('error');
          setMessage(serverMsg);
        }
      }
    } catch {
      setStatus('error');
      setMessage('网络错误，请检查网络连接后重试');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5" data-readdy-form="">
      <div className="contact-alt-wrapper">
        <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
            姓名 <span className="text-primary-500">*</span>
          </label>
          <input
            id="contact-name"
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            placeholder="请输入您的姓名"
            className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
          />
        </div>
        <div>
          <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
            邮箱 <span className="text-primary-500">*</span>
          </label>
          <input
            id="contact-email"
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            placeholder="请输入您的邮箱地址"
            className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
          />
        </div>
      </div>

      <div>
        <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
          主题 <span className="text-primary-500">*</span>
        </label>
        <input
          id="contact-subject"
          type="text"
          name="subject"
          value={formData.subject}
          onChange={handleChange}
          required
          placeholder="请选择或输入主题"
          className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
        />
      </div>

      <div>
        <label htmlFor="contact-content" className="block text-sm font-medium text-foreground-700 mb-1.5">
          留言内容 <span className="text-primary-500">*</span>
        </label>
        <textarea
          id="contact-content"
          name="content"
          value={formData.content}
          onChange={handleChange}
          required
          maxLength={500}
          rows={4}
          placeholder="请输入您的留言内容（限500字）"
          className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors resize-none"
        />
        <p className="text-xs text-foreground-500 mt-1">
          已输入 {formData.content.length}/500 字
        </p>
      </div>

      {status === 'success' && (
        <div className="p-3 rounded-lg bg-accent-100/70 text-accent-700 text-sm">
          <i className="ri-check-line mr-1"></i>
          {message}
        </div>
      )}

      {status === 'error' && (
        <div className="p-3 rounded-lg bg-primary-100/70 text-primary-700 text-sm">
          <i className="ri-error-warning-line mr-1"></i>
          {message}
        </div>
      )}

      <button
        type="submit"
        disabled={status === 'loading'}
        className="w-full py-3 bg-primary-500 text-background-50 rounded-lg font-medium hover:bg-primary-600 transition-colors disabled:opacity-60 whitespace-nowrap text-sm"
      >
        {status === 'loading' ? '提交中...' : '发送留言'}
      </button>
    </form>
  );
}